import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, ArrowUpDown, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const searchSchema = z.object({
  q: fallback(z.string(), "").default(""),
  status: fallback(z.enum(["all", "draft", "published"]), "all").default("all"),
  page: fallback(z.number().int().min(1), 1).default(1),
  pageSize: fallback(z.union([z.literal(10), z.literal(20), z.literal(50), z.literal(100)]), 20).default(20),
  sortKey: fallback(z.enum(["title", "status", "created_at"]).optional(), undefined).default(undefined),
  sortDir: fallback(z.enum(["asc", "desc"]), "desc").default("desc"),
});

type SearchState = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/admin/blog")({
  validateSearch: zodValidator(searchSchema),
  component: AdminBlog,
});

type BlogForm = {
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  featured_image: string;
  status: string;
  tags: string;
  seo_title: string;
  seo_description: string;
};

const emptyForm: BlogForm = {
  title: "", slug: "", content: "", excerpt: "", featured_image: "",
  status: "draft", tags: "", seo_title: "", seo_description: "",
};

const statusLabels: Record<string, string> = {
  draft: "Kladde",
  published: "Udgivet",
};

function AdminBlog() {
  const queryClient = useQueryClient();
  const urlSearch = Route.useSearch();
  const navigate = useNavigate({ from: "/admin/blog" });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<BlogForm>(emptyForm);

  const { data: posts, isLoading } = useQuery({
    queryKey: ["admin-blog"],
    queryFn: async () => {
      const { data } = await supabase.from("blog_posts").select("*").order("created_at", { ascending: false });
      return data || [];
    },
  });

  const filtered = useMemo(() => {
    let list = posts || [];
    if (urlSearch.status !== "all") {
      list = list.filter((p) => p.status === urlSearch.status);
    }
    const q = urlSearch.q.trim().toLowerCase();
    if (q) {
      list = list.filter((p) =>
        [p.title, p.slug, p.excerpt, (p.tags || []).join(" ")]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(q))
      );
    }
    if (urlSearch.sortKey) {
      const dir = urlSearch.sortDir === "asc" ? 1 : -1;
      list = [...list].sort((a, b) => {
        let av: any = a[urlSearch.sortKey as keyof typeof a];
        let bv: any = b[urlSearch.sortKey as keyof typeof b];
        if (urlSearch.sortKey === "created_at") {
          av = new Date(av || 0).getTime();
          bv = new Date(bv || 0).getTime();
        } else {
          av = String(av ?? "").toLowerCase();
          bv = String(bv ?? "").toLowerCase();
        }
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    }
    return list;
  }, [posts, urlSearch.q, urlSearch.status, urlSearch.sortKey, urlSearch.sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / urlSearch.pageSize));
  const currentPage = Math.min(urlSearch.page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * urlSearch.pageSize, currentPage * urlSearch.pageSize);

  const setSearch = (q: string) => {
    navigate({ search: (prev: SearchState) => ({ ...prev, q, page: 1 }), replace: true });
  };
  const setStatus = (status: SearchState["status"]) => {
    navigate({ search: (prev: SearchState) => ({ ...prev, status, page: 1 }), replace: true });
  };
  const setPage = (page: number) => {
    navigate({ search: (prev: SearchState) => ({ ...prev, page }), replace: true });
  };
  const setPageSize = (pageSize: 10 | 20 | 50 | 100) => {
    navigate({ search: (prev: SearchState) => ({ ...prev, pageSize, page: 1 }), replace: true });
  };
  const toggleSort = (key: NonNullable<SearchState["sortKey"]>) => {
    navigate({
      search: (prev: SearchState) => {
        if (prev.sortKey !== key) return { ...prev, sortKey: key, sortDir: "asc" as const, page: 1 };
        if (prev.sortDir === "asc") return { ...prev, sortDir: "desc" as const, page: 1 };
        return { ...prev, sortKey: undefined, sortDir: "desc" as const, page: 1 };
      },
      replace: true,
    });
  };

  const SortIcon = ({ col }: { col: NonNullable<SearchState["sortKey"]> }) => {
    if (urlSearch.sortKey !== col) return <ArrowUpDown size={12} className="opacity-40" />;
    return urlSearch.sortDir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
  };

  const saveMutation = useMutation({
    mutationFn: async (data: BlogForm) => {
      const payload = {
        title: data.title,
        slug: data.slug || data.title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""),
        content: data.content || null,
        excerpt: data.excerpt || null,
        featured_image: data.featured_image || null,
        status: data.status as any,
        tags: data.tags ? data.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
        seo_title: data.seo_title || null,
        seo_description: data.seo_description || null,
        published_at: data.status === "published" ? new Date().toISOString() : null,
      };

      if (editId) {
        await supabase.from("blog_posts").update(payload as any).eq("id", editId);
      } else {
        await supabase.from("blog_posts").insert(payload as any);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-blog"] });
      setDialogOpen(false);
      setEditId(null);
      setForm(emptyForm);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from("blog_posts").delete().eq("id", id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-blog"] }),
  });

  const openEdit = (post: any) => {
    setEditId(post.id);
    setForm({
      title: post.title, slug: post.slug, content: post.content || "", excerpt: post.excerpt || "",
      featured_image: post.featured_image || "", status: post.status, tags: (post.tags || []).join(", "),
      seo_title: post.seo_title || "", seo_description: post.seo_description || "",
    });
    setDialogOpen(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Blog</h1>
          <p className="mt-1 text-sm text-muted-foreground">Administrer blogindlæg</p>
        </div>
        <Button onClick={() => { setEditId(null); setForm(emptyForm); setDialogOpen(true); }} className="bg-accent text-accent-foreground hover:opacity-90">
          <Plus size={16} className="mr-2" /> Nyt indlæg
        </Button>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={urlSearch.q}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Søg titel, slug, uddrag, tags..."
            className="pl-9"
          />
        </div>
        <select
          value={urlSearch.status}
          onChange={(e) => setStatus(e.target.value as SearchState["status"])}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="all">Alle statusser</option>
          <option value="draft">Kladde</option>
          <option value="published">Udgivet</option>
        </select>
      </div>

      <div className="mt-4 overflow-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                <button onClick={() => toggleSort("title")} className="inline-flex items-center gap-1.5 hover:text-foreground">
                  Titel <SortIcon col="title" />
                </button>
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                <button onClick={() => toggleSort("status")} className="inline-flex items-center gap-1.5 hover:text-foreground">
                  Status <SortIcon col="status" />
                </button>
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                <button onClick={() => toggleSort("created_at")} className="inline-flex items-center gap-1.5 hover:text-foreground">
                  Dato <SortIcon col="created_at" />
                </button>
              </th>
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">Handlinger</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Indlæser...</td></tr>
            ) : pageItems.length > 0 ? (
              pageItems.map((post) => (
                <tr key={post.id} className="border-t border-border hover:bg-muted/50">
                  <td className="px-4 py-3 font-medium text-foreground">{post.title}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${post.status === "published" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>
                      {statusLabels[post.status] || post.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{new Date(post.created_at).toLocaleDateString("da-DK")}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(post)} className="mr-2 text-muted-foreground hover:text-foreground"><Pencil size={16} /></button>
                    <button onClick={() => { if (confirm("Slet dette indlæg?")) deleteMutation.mutate(post.id); }} className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Ingen indlæg matcher</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {filtered.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>
              Viser {(currentPage - 1) * urlSearch.pageSize + 1}–
              {Math.min(currentPage * urlSearch.pageSize, filtered.length)} af {filtered.length}
            </span>
            <select
              value={urlSearch.pageSize}
              onChange={(e) => setPageSize(Number(e.target.value) as 10 | 20 | 50 | 100)}
              className="rounded-md border border-input bg-background px-2 py-1 text-xs"
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>{n} pr. side</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(currentPage - 1)} disabled={currentPage <= 1}>Forrige</Button>
            <span>Side {currentPage} af {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(currentPage + 1)} disabled={currentPage >= totalPages}>Næste</Button>
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? "Rediger indlæg" : "Nyt blogindlæg"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(form); }} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="mb-1 block text-xs text-muted-foreground">Titel *</label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
              <div><label className="mb-1 block text-xs text-muted-foreground">Slug</label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-genereret" /></div>
            </div>
            <div><label className="mb-1 block text-xs text-muted-foreground">Uddrag</label><Textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} rows={2} /></div>
            <div><label className="mb-1 block text-xs text-muted-foreground">Indhold</label><Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={12} /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="mb-1 block text-xs text-muted-foreground">Billede URL</label><Input value={form.featured_image} onChange={(e) => setForm({ ...form, featured_image: e.target.value })} /></div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <option value="draft">Kladde</option><option value="published">Udgivet</option>
                </select>
              </div>
            </div>
            <div><label className="mb-1 block text-xs text-muted-foreground">Tags (kommasepareret)</label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="biler, tips, nyheder" /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="mb-1 block text-xs text-muted-foreground">SEO Titel</label><Input value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} /></div>
              <div><label className="mb-1 block text-xs text-muted-foreground">SEO Beskrivelse</label><Input value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} /></div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuller</Button>
              <Button type="submit" disabled={saveMutation.isPending} className="bg-accent text-accent-foreground hover:opacity-90">
                {saveMutation.isPending ? "Gemmer..." : editId ? "Opdater" : "Opret"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
